import { useMatch, useNavigate, useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getVendors } from 'MediaManager/services'
import { getPackage, upsertPackage } from 'MediaManager/services/packages'
import React, { useCallback, useEffect, useMemo } from 'react'
import DataTable from 'shared/components/DataTable'
import Select from 'shared/components/Select'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'
import ItemsTable from './components/ItemsTable'
import AssociatedFloorPlans from './components/FloorPlans'
import { root } from 'postcss'
import Button from 'shared/components/Button'
import CreateFromDesignLayout from './components/CreateFromDesignLayout'
import { toast } from 'react-toastify'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'

function EditPackageScreen(props) {
  const { id, design_layout } = useSearch()

  

  const [resultValue, setResultValue] = React.useState(null)

  const search = useSearch()

  const getPackageData = useCallback(async () => {
    if (!id) {
      return {
        unit_plans: [],
        room_classifications: [],
        vendor: {
          id: null,
          display_name: '',
          name: '',
        },
      }
    }
    const { data } = await getPackage(id)
    return data.packages[0]
  }, [id])

  const { data: pack, refresh } = useRequest(getPackageData)

  const getVendorsData = useCallback(async () => {
    const { data } = await getVendors()
    return data.vendors
  }, [])

  const { data: vendors } = useRequest(getVendorsData)

  const vendorOptions = useMemo(() => {
    if (vendors) {
      return vendors.map((v) => ({
        label: v.full_name,
        value: v.id,
      }))
    }
    return []
  }, [vendors])

  useEffect(() => {
    if (pack) {
      formik.setValues({
        id: pack.id,
        display_name: pack.display_name,
        name: pack.name,
        vendor: pack.vendor.id,
        price: pack.price,
      })
    }
  }, [pack])

  const validationSchema = Yup.object().shape({
    vendor: Yup.number().required(errorMessages.required),
    display_name: Yup.string().required(errorMessages.required),
    name: Yup.string().required(errorMessages.required),
    price: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        const result = await upsertPackage({
          ...(id ? { id } : {}),
          vendor_id: formik.values.vendor,
          display_name: formik.values.display_name,
          name: formik.values.name,
          price: parseFloat(formik.values.price),
        })
        if (id) {
          toast.success('Package updated successfully', { theme: 'colored' })
        } else {
          toast.success('Package created successfully', { theme: 'colored' })
          if (!search?.design_layout_id) {
            navigate({ to: `/packages/edit?id=${result.data.upsert_packages[0].id}&created=true`, replace: true })
          } else {
            navigate({ to: `/packages/edit${window.location.search}&id=${result.data.upsert_packages[0].id}&created=true`, replace: true })
          }
        }
        // setResultValue('success')
      } catch (error) {
        console.error('Error saving package:', error)
        setResultValue('error')
      }
    },
  })

  const navigate = useNavigate()

  function getFieldError(field) {
    return formik.touched[field] && formik.errors[field]
  }

  return (
    <ScreenLayout title={id ? 'Edit Package' : 'Create Package'}>
      {pack && (
        <>
          <form onSubmit={formik.handleSubmit} className="w-full relative">
            <div className="grid grid-cols-3 gap-[16px]">
              {id && (
                <div>
                  <Input label={'ID'} value={formik.values.id} disabled />
                </div>
              )}
              <div>
                <Input
                  label={'Name'}
                  required
                  name="name"
                  error={getFieldError('name')}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div>
                <Input
                  label={'Display name'}
                  required
                  name="display_name"
                  error={getFieldError('display_name')}
                  value={formik.values.display_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div>
                <Select
                  options={vendorOptions}
                  required
                  error={getFieldError('vendor')}
                  value={vendorOptions.find((v) => v.value === formik.values.vendor)}
                  onChange={(val) => formik.setFieldValue('vendor', val.value)}
                  label="Vendor"
                />
              </div>
              <div>
                <Input
                  label={'Price (USD)'}
                  name="price"
                  number
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
              </div>
              <div>
                <Input label={'Type'} name="type" value="CSV" disabled />
              </div>
            </div>
            <div className="flex items-center justify-end mt-[32px]">
              <Button size="small" type="submit" disabled={!formik.isValid} rounded variant="primary">
                {id ? 'Save' : 'Create package'}
              </Button>
            </div>
          </form>
          <div className={`${!id ? 'opacity-50 pointer-events-none' : ''}`}>
            {id && (
              <div className="mt-[32px]">
                <ItemsTable
                  pack={pack}
                  onDataChange={() => {
                    refresh()
                  }}
                />
              </div>
            )}
            <div className="mt-[32px]">
              <AssociatedFloorPlans pack={pack} />
            </div>
          </div>
          <div className="flex items-center justify-end mt-[32px]">
            <Button
              rounded
              className="ml-4"
              variant="secondary"
              size="large"
              onClick={() => {
                if (search?.design_layout_id && search?.id) {
                  navigate({ to: `/design_layouts/edit?line=${search.line_id}&id=${search.design_layout_id}` })
                } else {
                  window.history.back()
                }
              }}
            >
              Cancel
            </Button>
          </div>

          {!id && (
            <div className={`mt-[16px] w-full flex items-center justify-between`}>
              <CreateFromDesignLayout
                params={search}
                onSuccess={(id) => {
                  if (search?.design_layout_id) {
                    window.history.back()
                  } else {
                    navigate({ to: `/packages/edit?id=${id}` })
                  }
                }}
              />
            </div>
          )}
        </>
      )}

      {resultValue && (
        <ActionSuccessModal
          action="Package"
          state={resultValue}
          skipConfirm
          skipBack
          onClose={() => {
            if (resultValue === 'success') {
              window.history.back()
            } else {
              setResultValue(null)
            }
          }}
        />
      )}
    </ScreenLayout>
  )
}

export default EditPackageScreen
